import { getStringTime, daysFormat, dateFormat } from '@/filters'
import {
  setCheckButtons,
  setTableButtons,
  setFormButtons
} from '@/utils/getbuttons.js'
import {
  isLaborCompanyOrg,
  getLaborParentBidUid,
  getLaborCompanyName
} from '@/utils/bidLevelActions.js'
import { hasDataReportPermission } from '@/utils/platformPages.js'
import { checkInNumError, parseNonNegInt, teamOnSiteCap, inputCheckInLabel, crowdCappedByTeamMessage } from '@/utils/pageParams/team.js'

function formatDateTime(time) {
  const date = time instanceof Date ? time : new Date(time)
  if (isNaN(date.getTime())) return ''
  return daysFormat(date) + ' ' + dateFormat(date)
}

function defaultPlanTime() {
  return formatDateTime(Date.now() + 2 * 60 * 1000)
}

function parseEduTime(value) {
  if (!value) return NaN
  const date = value instanceof Date ? value : new Date(String(value).replace(/-/g, '/'))
  return date.getTime()
}

/** 提交时若计划时间已过期，重置为当前提交时间 */
export function resolveSubmitPlanTime(value) {
  const picked = parseEduTime(value)
  if (isNaN(picked) || picked < Date.now() - 1000) {
    return formatDateTime(Date.now())
  }
  const text = String(value == null ? '' : value).trim()
  return text || formatDateTime(picked)
}

/** 计划培训时间起满 1 个自然月后，切片已清，仅前端展示，不改执行状态 */
export function isEduVideoExpired(rowOrTime) {
  const time =
    rowOrTime && typeof rowOrTime === 'object'
      ? rowOrTime.trainingTime || rowOrTime.training_time
      : rowOrTime
  const plan = parseEduTime(time)
  if (isNaN(plan)) return false
  const expire = new Date(plan)
  expire.setMonth(expire.getMonth() + 1)
  return Date.now() >= expire.getTime()
}

export function isEduTrainingVideoCleared(row) {
  if (!row) return false
  const videos = row.trainingVideos
  let hasVideo = false
  if (Array.isArray(videos)) {
    hasVideo = videos.some((item) => {
      if (item && typeof item === 'object') return !!(item.uid || item.videoUid)
      return String(item || '').trim() && String(item).trim() !== '暂无影像'
    })
  } else if (typeof videos === 'string') {
    const text = videos.trim()
    hasVideo = !!(text && text !== '暂无影像' && text !== '{}' && text !== '[]' && text !== 'null')
  }
  return hasVideo && isEduVideoExpired(row)
}

function isUnchangedEduTime(value, original) {
  const next = parseEduTime(value)
  const prev = parseEduTime(original)
  if (isNaN(next) || isNaN(prev)) return false
  return Math.abs(next - prev) <= 1000
}

export function extractEduFilterKeywords(text) {
  const raw = String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
  if (!raw) return ''
  const stop = /班组|驻地|公司|有限|工程|主要|作业内容|作业部位|备注|以及|或者/
  const parts = raw
    .split(/[\s,，、；;。./／|()（）]+/)
    .map((item) => item.replace(stop, '').trim())
    .filter((item) => item.length >= 2)
  if (!parts.length) return raw.length <= 16 ? raw : raw.slice(0, 8)
  parts.sort((a, b) => b.length - a.length)
  return parts[0]
}

export function fillEduFromWorkTree(form, tree) {
  const data = formatEduFromWorkTree(tree)
  form.workContents = data.workContents
  form.workContent = data.workContent
  form.trainingBriefs = data.trainingBriefs
  form.contentPaths = data.contentPaths
  form.trainingBrief = data.trainingBrief
  form.selectedWorkPaths = data.selectedWorkPaths
}

export function nestEduWorkTree(rows) {
  const roots = []
  const source = rows || []
  source.forEach((item) => {
    const parts = []
    String(item.label || '')
      .split('/')
      .forEach((part) => {
        const text = part.trim()
        if (text) parts.push(text)
      })
    if (!parts.length) {
      const typeParts = [item.riskProType, item.riskWokeType, item.riskSource]
      typeParts.forEach((part) => {
        const text = String(part || '').trim()
        if (text) parts.push(text)
      })
    }
    if (!parts.length) return
    if (parts.some((part) => /^[A-Za-z0-9_]+$/.test(part) && /[A-Za-z]/.test(part))) return
    let list = roots
    let node = null
    parts.forEach((part, index) => {
      node = list.find((row) => row.label === part)
      if (!node) {
        node = { label: part, value: part, children: [] }
        list.push(node)
      }
      if (index === parts.length - 1) {
        node.value = part
        const prevKids = (node.children || []).slice()
        const seen = {}
        const children = []
        prevKids.concat(item.children || []).forEach((child) => {
          const childLabel = String((child && child.label) || '').trim()
          if (!childLabel || seen[childLabel]) return
          seen[childLabel] = true
          children.push(Object.assign({}, child, { label: childLabel }))
        })
        node.source = Object.assign({}, item, { children })
        delete node.children
      } else {
        if (!node.children) node.children = []
        list = node.children
      }
    })
  })
  return roots
}

export function nestEduContentTree(rows) {
  const roots = []
  const source = rows || []
  source.forEach((item) => {
    const parts = []
    if (item.riskProType || item.riskWokeType || item.riskSource) {
      const typeParts = [item.riskProType, item.riskWokeType, item.riskSource]
      typeParts.forEach((part) => {
        const text = String(part || '').trim()
        if (text) parts.push(text)
      })
    } else {
      String(item.label || '')
        .split('/')
        .forEach((part) => {
          const text = part.trim()
          if (text) parts.push(text)
        })
    }
    if (!parts.length) return
    let list = roots
    let node = null
    parts.forEach((part) => {
      node = list.find((row) => row.label === part)
      if (!node) {
        node = { label: part, children: [] }
        list.push(node)
      }
      list = node.children
    })
    ;(item.children || []).forEach((child) => {
      const label = child && child.label
      if (!label || !node) return
      if (!node.children.some((row) => row.label === label)) {
        node.children.push({
          label,
          value: child.value || child.uid || label
        })
      }
    })
  })
  return roots
}

export function toEduCascadePaths(paths) {
  return (paths || [])
    .map((path) => {
      if (!Array.isArray(path) || path.length < 2) return null
      const child = String(path[path.length - 1] || '').trim()
      const parents = path
        .slice(0, -1)
        .join('/')
        .split('/')
        .map((item) => item.trim())
        .filter(Boolean)
      if (!child || !parents.length) return null
      return parents.concat(child)
    })
    .filter(Boolean)
}

export function applyEduCascadePaths(form, paths, dictTree) {
  replaceEduCascadePaths(form, paths, dictTree)
}

export function parseEduWorkContentItems(text) {
  if (!text) return []
  return String(text)
    .split(/[；;]\r?\n|[；;](?![\r\n])/g)
    .map((item) =>
      String(item || '')
        .replace(/^[；;,\s]+|[；;,\s]+$/g, '')
        .trim()
    )
    .filter(Boolean)
}

function uniqueEduStrings(list) {
  const seen = {}
  const out = []
  ;(list || []).forEach((item) => {
    const text = String(item || '').trim()
    if (!text || seen[text]) return
    seen[text] = true
    out.push(text)
  })
  return out
}

function eduPathKey(path) {
  if (Array.isArray(path)) {
    return path
      .map((item) => String(item || '').trim())
      .filter(Boolean)
      .join('\u0001')
  }
  return String(path || '').trim()
}

export function mergeEduPaths(prev, next) {
  const seen = {}
  const out = []
  ;(prev || []).concat(next || []).forEach((path) => {
    const key = eduPathKey(path)
    if (!key || seen[key]) return
    seen[key] = true
    out.push(
      Array.isArray(path)
        ? path.map((item) => String(item || '').trim()).filter(Boolean)
        : [String(path).trim()]
    )
  })
  return out
}

function workLabelToPath(label) {
  return String(label || '')
    .split('/')
    .map((part) => part.trim())
    .filter(Boolean)
}

function uniqueWorkRows(tree) {
  const map = {}
  const list = []
  ;(tree || []).forEach((item) => {
    const label = String((item && item.label) || '').trim()
    if (!label) return
    if (!map[label]) {
      map[label] = { label, children: [] }
      list.push(map[label])
    }
    const children = map[label].children
    ;(item.children || []).forEach((child) => {
      const childLabel = String((child && child.label) || '').trim()
      if (!childLabel) return
      if (!children.some((row) => row.label === childLabel)) {
        children.push({ label: childLabel })
      }
    })
  })
  return list
}

export function formatEduFromWorkTree(tree) {
  const rows = uniqueWorkRows(tree)
  const workContents = rows.map((item) => item && item.label).filter(Boolean)
  const trainingBriefs = []
  const contentPaths = []
  const blocks = rows.map((item, index) => {
    const children = item.children || []
    children.forEach((child) => {
      if (child && child.label) {
        trainingBriefs.push(child.label)
        contentPaths.push([item.label, child.label])
      }
    })
    const lines = children.map((child, num) => '（' + (num + 1) + '）' + (child.label || ''))
    return index + 1 + '、' + item.label + '：\n' + lines.join('；\n')
  })
  return {
    workContents,
    workContent: workContents.join('；\n'),
    trainingBriefs,
    contentPaths,
    trainingBrief: blocks.join('\n'),
    selectedWorkPaths: workContents.map(workLabelToPath).filter((path) => path.length)
  }
}

export function groupsFromEduPaths(paths) {
  const groups = []
  ;(paths || []).forEach((path) => {
    if (!Array.isArray(path) || path.length < 2) return
    const child = String(path[path.length - 1] || '').trim()
    const parent = path
      .slice(0, -1)
      .map((item) => String(item || '').trim())
      .filter(Boolean)
      .join('/')
    if (!parent || !child) return
    let group = groups.find((item) => item.label === parent)
    if (!group) {
      group = { label: parent, children: [] }
      groups.push(group)
    }
    if (!group.children.some((row) => row.label === child)) {
      group.children.push({ label: child })
    }
  })
  return groups
}

function parseStructuredEduBrief(text) {
  const raw = String(text || '').replace(/\r\n/g, '\n')
  if (!raw.trim()) return []
  const headerRe = /^(\d+)[、.．]\s*(.+?)\s*[：:]\s*$/gm
  const headers = []
  let match
  while ((match = headerRe.exec(raw))) {
    headers.push({
      parent: String(match[2] || '').trim(),
      end: match.index + match[0].length,
      index: match.index
    })
  }
  return headers
    .map((item, i) => {
      const from = item.end
      const to = i + 1 < headers.length ? headers[i + 1].index : raw.length
      const children = raw
        .slice(from, to)
        .split(/[；;]\r?\n|[；;](?![\r\n])|\n/g)
        .map((row) =>
          String(row || '')
            .replace(/^[（(]\d+[）)]\s*/, '')
            .replace(/^[；;,\s]+|[；;,\s]+$/g, '')
            .trim()
        )
        .filter(Boolean)
      return {
        parent: item.parent,
        children
      }
    })
    .filter((item) => item.parent)
}

function parseEduBriefBlocks(text) {
  const parsed = parseStructuredEduBrief(text)
  if (parsed.length) return parsed
  const raw = String(text || '').replace(/\r\n/g, '\n')
  if (!raw.trim()) return []
  const headerRe = /^(\d+)[、.．](.+?)[：:](.*)$/gm
  const headers = []
  let match
  while ((match = headerRe.exec(raw))) {
    const parent = String(match[2] || '').trim()
    if (!parent) continue
    const rest = String(match[3] || '')
    headers.push({
      parent,
      end: match.index + match[0].length - rest.length,
      index: match.index
    })
  }
  return headers
    .map((item, i) => {
      const from = item.end
      const to = i + 1 < headers.length ? headers[i + 1].index : raw.length
      const children = raw
        .slice(from, to)
        .split(/[；;]\r?\n|[；;](?![\r\n])|\n/g)
        .map((row) =>
          String(row || '')
            .replace(/^[（(]\d+[）)]\s*/, '')
            .replace(/^[；;,\s]+|[；;,\s]+$/g, '')
            .trim()
        )
        .filter(Boolean)
      return {
        parent: item.parent,
        children
      }
    })
    .filter((item) => item.parent)
}

function normalizeEduTitle(text) {
  return String(text || '')
    .replace(/^\d+[、.．]\s*/, '')
    .replace(/[：:]\s*$/, '')
    .trim()
}

function formatEduBlocks(blocks) {
  return (blocks || [])
    .map((block, index) => {
      const lines = (block.children || []).map(
        (child, num) => '（' + (num + 1) + '）' + child
      )
      return index + 1 + '、' + block.parent + '：\n' + lines.join('；\n')
    })
    .join('\n')
}

function eduTitlesMatch(a, b) {
  const left = normalizeEduTitle(a)
  const right = normalizeEduTitle(b)
  if (!left || !right) return false
  if (left === right) return true
  const leftLeaf = left.split('/').pop()
  const rightLeaf = right.split('/').pop()
  if (leftLeaf && rightLeaf && leftLeaf === rightLeaf) return true
  return false
}

function findEduBlockForWorkItem(blocks, item, used) {
  const key = normalizeEduTitle(item)
  if (!key) return null
  let hit = (blocks || []).find(
    (block) =>
      !used[normalizeEduTitle(block.parent)] &&
      normalizeEduTitle(block.parent) === key
  )
  if (hit) return hit
  const leaf = key.split('/').pop()
  hit = (blocks || []).find(
    (block) =>
      !used[normalizeEduTitle(block.parent)] &&
      normalizeEduTitle(block.parent) === leaf
  )
  if (hit) return hit
  return (
    (blocks || []).find((block) => {
      const parent = normalizeEduTitle(block.parent)
      if (!parent || used[parent]) return false
      return eduTitlesMatch(key, parent)
    }) || null
  )
}

function wasEduBlockLinkedToWork(block, workItems) {
  return (workItems || []).some((item) => eduTitlesMatch(item, block && block.parent))
}

function rebuildEduBriefFields(form, blocks, workItems) {
  const kept = blocks || []
  form.trainingBrief = formatEduBlocks(kept)
  form.trainingBriefs = []
  form.contentPaths = []
  kept.forEach((block) => {
    const kids = block.children || []
    kids.forEach((child) => {
      if (!child) return
      form.trainingBriefs.push(child)
      form.contentPaths.push([block.parent, child])
    })
  })
  const items = workItems || parseEduWorkContentItems(form.workContent)
  form.workContents = items
  form.selectedWorkPaths = items.map(workLabelToPath).filter((path) => path.length)
}

/** 作业内容删掉某一项时，同步去掉教育内容里同标题整段；未对齐的自定义/AI 段保留 */
export function pruneEduBriefByWorkItems(form, prevWorkItems) {
  if (!form) return false
  const items = parseEduWorkContentItems(form.workContent)
  const prevItems =
    prevWorkItems && prevWorkItems.length ? prevWorkItems : items
  const blocks = parseEduBriefBlocks(form.trainingBrief)
  if (!blocks.length) {
    form.workContents = items
    form.selectedWorkPaths = items.map(workLabelToPath).filter((path) => path.length)
    return false
  }
  if (!items.length) {
    form.workContents = []
    form.trainingBrief = ''
    form.trainingBriefs = []
    form.contentPaths = []
    form.selectedWorkPaths = []
    return true
  }
  const used = {}
  const kept = []
  items.forEach((item) => {
    const hit = findEduBlockForWorkItem(blocks, item, used)
    if (!hit) return
    used[normalizeEduTitle(hit.parent)] = true
    kept.push(hit)
  })
  blocks.forEach((block) => {
    const parent = normalizeEduTitle(block.parent)
    if (!parent || used[parent]) return
    if (wasEduBlockLinkedToWork(block, prevItems)) return
    used[parent] = true
    kept.push(block)
  })
  rebuildEduBriefFields(form, kept, items)
  return true
}

export function applyWorkContentEdit(form, tree) {
  if (!form) return false
  const prev = (form.workContents || []).slice()
  form.workContents = parseEduWorkContentItems(form.workContent)
  if (!String(form.trainingBrief || '').trim()) {
    const matched = (tree || []).filter((item) => {
      return form.workContents.indexOf(item.label) >= 0
    })
    if (matched.length) fillEduFromWorkTree(form, matched)
  } else {
    pruneEduBriefByWorkItems(form, prev)
  }
  inferEduSelectIntoForm(form, tree)
  return true
}

function dictHasWorkLabel(dict, label) {
  const key = normalizeEduTitle(label)
  if (!key) return false
  if (dict[label] || dict[key]) return true
  const leaf = key.split('/').pop()
  if (leaf && dict[leaf]) return true
  return Object.keys(dict || {}).some((name) => eduTitlesMatch(name, key))
}

function resolveDictKids(dict, parent) {
  if (!dict || !parent) return null
  if (dict[parent]) return dict[parent]
  const key = normalizeEduTitle(parent)
  if (key && dict[key]) return dict[key]
  const leaf = key ? key.split('/').pop() : ''
  if (leaf && dict[leaf]) return dict[leaf]
  const hit = Object.keys(dict).find((name) => eduTitlesMatch(name, key))
  return hit ? dict[hit] : null
}

function echoChildKey(parent, child) {
  const p = normalizeEduTitle(parent)
  const c = String(child || '').trim()
  if (!p || !c) return ''
  return p + '\u0001' + c
}

function echoChildSet(paths) {
  const set = {}
  ;(paths || []).forEach((path) => {
    if (!Array.isArray(path) || path.length < 2) return
    const child = String(path[path.length - 1] || '').trim()
    const parent = path
      .slice(0, -1)
      .map((item) => String(item || '').trim())
      .filter(Boolean)
      .join('/')
    if (!parent || !child) return
    set[echoChildKey(parent, child)] = true
    const leaf = parent.split('/').pop()
    if (leaf && leaf !== parent) set[echoChildKey(leaf, child)] = true
  })
  return set
}

/**
 * 仅替换「此前已从字典勾选回显」的子句；AI/手动正文即使措辞接近字典也保留。
 * prevEchoPaths 为空（如 AI 草稿清空了 contentPaths）时，上一稿全部当作不可覆盖。
 */
function mergeSelectedEduBlocks(selectedBlocks, prevBlocks, dict, prevEchoPaths) {
  const echoSet = echoChildSet(prevEchoPaths)
  const hasEcho = Object.keys(echoSet).length > 0
  const isReplaceableChild = (parent, child) => {
    if (!child) return false
    const dictKids = resolveDictKids(dict, parent) || {}
    if (!dictKids[child]) return false
    if (!hasEcho) return false
    return !!echoSet[echoChildKey(parent, child)]
  }
  const merged = (selectedBlocks || []).map((block) => {
    const prev = (prevBlocks || []).find((item) =>
      eduTitlesMatch(item.parent, block.parent)
    )
    const customKids = ((prev && prev.children) || []).filter((child) => {
      if (!child) return false
      if (isReplaceableChild(prev.parent, child)) return false
      if (isReplaceableChild(block.parent, child)) return false
      return true
    })
    return {
      parent: block.parent,
      children: uniqueEduStrings((block.children || []).concat(customKids))
    }
  })
  ;(prevBlocks || []).forEach((block) => {
    if (merged.some((item) => eduTitlesMatch(item.parent, block.parent))) return
    const kept = (block.children || []).filter(
      (child) => child && !isReplaceableChild(block.parent, child)
    )
    if (!kept.length) return
    merged.push({
      parent: block.parent,
      children: kept
    })
  })
  return merged
}

function appendUnparsedBrief(form, rawText) {
  const custom = String(rawText || '').trim()
  if (!custom) return
  const built = String((form && form.trainingBrief) || '').trim()
  if (!built) {
    form.trainingBrief = custom
    return
  }
  if (built.indexOf(custom) >= 0) return
  form.trainingBrief = built + '\n' + custom
}

function orderEduBlocksByWork(blocks, workItems) {
  const order = workItems || []
  return (blocks || [])
    .slice()
    .sort((a, b) => {
      const ia = order.findIndex((item) => eduTitlesMatch(item, a.parent))
      const ib = order.findIndex((item) => eduTitlesMatch(item, b.parent))
      if (ia < 0 && ib < 0) return 0
      if (ia < 0) return 1
      if (ib < 0) return -1
      return ia - ib
    })
}

/**
 * 字典选择作业内容：以本次勾选替换字典部分，仅保留无法回显的自定义/AI 片段。
 */
export function applyEduWorkSelect(form, selectedRows, dictTree) {
  if (!form) return
  const dict = treeLabelSet(dictTree || selectedRows || [])
  const selected = formatEduFromWorkTree(selectedRows || [])
  const customWork = parseEduWorkContentItems(form.workContent).filter(
    (label) => label && !dictHasWorkLabel(dict, label)
  )
  form.workContents = uniqueEduStrings(selected.workContents.concat(customWork))
  form.workContent = form.workContents.length
    ? form.workContents.join('；\n')
    : ''
  form.selectedWorkPaths = selected.selectedWorkPaths

  const prevText = String(form.trainingBrief || '').trim()
  const prevBlocks = parseEduBriefBlocks(prevText)
  const unparsedCustom = !prevBlocks.length && prevText ? prevText : ''
  const prevEchoPaths = (form.contentPaths || []).slice()
  const selectedBlocks = parseEduBriefBlocks(selected.trainingBrief)
  const mergedBlocks = orderEduBlocksByWork(
    mergeSelectedEduBlocks(selectedBlocks, prevBlocks, dict, prevEchoPaths),
    form.workContents
  )
  rebuildEduBriefFields(form, mergedBlocks, form.workContents)
  form.contentPaths = selected.contentPaths
  appendUnparsedBrief(form, unparsedCustom)
}

/** @deprecated 选择回填请用 applyEduWorkSelect；保留导出以免旧调用报错 */
export function appendEduFromWorkTree(form, tree, dictTree) {
  applyEduWorkSelect(form, tree, dictTree || tree)
}

/**
 * 字典选择教育内容：以本次勾选替换字典教育项，保留无法回显的自定义/AI 段。
 */
export function replaceEduCascadePaths(form, paths, dictTree) {
  if (!form) return
  const incoming = toEduCascadePaths(paths)
  const dict = treeLabelSet(dictTree || [])
  const prevText = String(form.trainingBrief || '').trim()
  const prevBlocks = parseEduBriefBlocks(prevText)
  const unparsedCustom = !prevBlocks.length && prevText ? prevText : ''
  const prevEchoPaths = (form.contentPaths || []).slice()
  const selectedBlocks = parseEduBriefBlocks(
    formatEduFromWorkTree(groupsFromEduPaths(incoming)).trainingBrief
  )
  const workItems =
    form.workContents && form.workContents.length
      ? form.workContents
      : parseEduWorkContentItems(form.workContent)
  const mergedBlocks = orderEduBlocksByWork(
    mergeSelectedEduBlocks(selectedBlocks, prevBlocks, dict, prevEchoPaths),
    workItems
  )
  rebuildEduBriefFields(form, mergedBlocks, workItems)
  form.contentPaths = incoming
  appendUnparsedBrief(form, unparsedCustom)
}

export function appendEduCascadePaths(form, paths, dictTree) {
  replaceEduCascadePaths(form, paths, dictTree)
}

function treeLabelSet(tree) {
  const map = {}
  ;(tree || []).forEach((item) => {
    const label = item && item.label
    if (!label) return
    const children = {}
    ;(item.children || []).forEach((child) => {
      if (child && child.label) children[child.label] = true
    })
    map[label] = children
  })
  return map
}

export function inferEduWorkPathsFromText(text, tree) {
  const labels = parseEduWorkContentItems(text)
  const dict = treeLabelSet(tree)
  return labels
    .filter((label) => dict[label])
    .map(workLabelToPath)
    .filter((path) => path.length)
}

export function inferEduContentPathsFromText(text, tree) {
  const dict = treeLabelSet(tree)
  const result = []
  const seen = {}
  parseStructuredEduBrief(text).forEach((block) => {
    const children = dict[block.parent]
    if (!children) return
    block.children.forEach((child) => {
      if (!children[child]) return
      const path = [block.parent, child]
      const key = eduPathKey(path)
      if (!key || seen[key]) return
      seen[key] = true
      result.push(path)
    })
  })
  return result
}

export function inferEduSelectIntoForm(form, tree) {
  if (!form) return
  const dict = treeLabelSet(tree)
  const workPaths = inferEduWorkPathsFromText(form.workContent, tree)
  parseStructuredEduBrief(form.trainingBrief).forEach((block) => {
    if (!dict[block.parent]) return
    workPaths.push(workLabelToPath(block.parent))
  })
  form.selectedWorkPaths = mergeEduPaths(form.selectedWorkPaths, workPaths)
  form.contentPaths = mergeEduPaths(
    form.contentPaths,
    inferEduContentPathsFromText(form.trainingBrief, tree)
  )
  const parents = (form.contentPaths || []).map((path) => path && path[0]).filter(Boolean)
  form.workContents = uniqueEduStrings(
    (form.workContents || []).concat(parseEduWorkContentItems(form.workContent), parents)
  )
}

export function echoEduContentPaths(form, tree) {
  return inferEduContentPathsFromText(form && form.trainingBrief, tree)
}

export function getEduExecuteStatus(row) {
  if (!row) return '未执行'
  const videos = row.trainingVideos
  let hasVideo = false
  if (Array.isArray(videos)) {
    hasVideo = videos.some((item) => {
      if (item && typeof item === 'object') return !!(item.uid || item.videoUid)
      return String(item || '').trim() && String(item).trim() !== '暂无影像'
    })
  } else if (typeof videos === 'string') {
    const text = videos.trim()
    hasVideo = !!(text && text !== '暂无影像' && text !== '{}' && text !== '[]' && text !== 'null')
  }
  const stored = String(row.executeStatus || row.execute_status || '').trim()
  const rectify =
    stored === '未整改' ||
    stored === '已整改' ||
    !!String(row.parentUid || row.parent_uid || '').trim()
  if (hasVideo) return rectify ? '已整改' : '已执行'
  return rectify ? '未整改' : '未执行'
}

export function isEduRectifyRecord(row) {
  const status = getEduExecuteStatus(row)
  return (
    status === '未整改' ||
    status === '已整改' ||
    !!(row && String(row.parentUid || row.parent_uid || '').trim())
  )
}

function shouldShowEduVideos(form, params) {
  if (!form) return false
  if (isEduRectifyRecord(form) || form.uid) return true
  return params && params.pageStatus === 'check'
}

function getEduCreateOrgUid(row) {
  if (!row) return ''
  return row.createOrgUid || row.create_org_uid || ''
}

function isEduOwner(params, row) {
  const orgValue = (params && params.orgValue) || ''
  const orgType = (params && params.orgType) || ''
  const data =
    row ||
    (params && (params.formValues || params.currentRow)) ||
    {}
  const creator = getEduCreateOrgUid(data)
  if (orgValue && creator && orgValue === creator) return true
  if (!orgValue || creator) return false
  if (
    isLaborCompanyOrg(orgType) &&
    String(data.laborCompanyUid || data.labor_company_uid || '') === orgValue
  ) {
    return true
  }
  if (
    orgType === 'simPro' &&
    String(data.orgUid || data.org_uid || '') === orgValue
  ) {
    return true
  }
  return false
}

function getEduForm(params, row) {
  return (
    row ||
    (params &&
      params.state &&
      params.state.formValues &&
      params.state.formValues.preClassEducation) ||
    (params && params.formValues) ||
    (params && params.currentRow) ||
    {}
  )
}

const EDU_FIXED_READONLY_KEYS = ['trainingNo', 'executeTime', 'coverageRate']
const EDU_UNRECTIFIED_KEYS = [
  'trainingTime',
  'trainingPerson',
  'workPlace',
  'workPlaceUid',
  'workContent',
  'trainingBrief'
]

export function canOpenEduEdit(row, params) {
  const data = getEduForm(params, row)
  const status = getEduExecuteStatus(data)
  if (status !== '未执行' && status !== '未整改') return false
  return canEditEdu(data, params)
}

function isEduBizLocked(params) {
  if (!params) return false
  if (params.pageStatus === 'add') return false
  return !canOpenEduEdit(getEduForm(params), params)
}

function isEduFieldLocked(params, fieldKey) {
  if (!params) return true
  if (params.pageStatus === 'check') return true
  if (fieldKey === 'checkInNum') return true
  if (fieldKey === 'trainingVideos') {
    return !canSignInEdu(params.orgType)
  }
  if (params.pageStatus === 'add') return false
  if (EDU_FIXED_READONLY_KEYS.indexOf(fieldKey) >= 0) return true
  if (isEduBizLocked(params)) return true
  if (getEduExecuteStatus(getEduForm(params)) === '未整改') {
    return EDU_UNRECTIFIED_KEYS.indexOf(fieldKey) < 0
  }
  return false
}

function isSuperAdmin(params) {
  return !!(params && params.userName === 'dpnadmin')
}

function isCreatorCurrentOrChildOrg(params, row) {
  if (isEduOwner(params, row)) return true
  const orgValue = String((params && params.orgValue) || '')
  const orgType = (params && params.orgType) || ''
  const data = row || {}
  const creator = String(getEduCreateOrgUid(data) || '')
  if (!orgValue || !creator || creator === orgValue) return false
  if (isLaborCompanyOrg(orgType)) return false
  const bidUid = String(data.orgUid || data.org_uid || '')
  const laborUid = String(
    data.laborCompanyUid || data.labor_company_uid || ''
  )
  if (creator !== bidUid && creator !== laborUid) {
    const bids =
      (params.state &&
        params.state.bidSection &&
        params.state.bidSection.subBidSection) ||
      []
    return bids.some((item) => {
      return String((item && (item.value || item.orgUid)) || '') === creator
    })
  }
  if (orgType === 'simPro') {
    return !bidUid || bidUid === orgValue
  }
  return true
}

/** 本机构或下级机构创建：编辑业务字段 */
function canEditEdu(row, params) {
  if (isSuperAdmin(params)) return true
  return isCreatorCurrentOrChildOrg(params, row)
}

function canDeleteEdu(row, params) {
  if (isSuperAdmin(params)) return true
  if (!params) return false
  const status = getEduExecuteStatus(row)
  if (status !== '未执行' && status !== '未整改') return false
  if (status === '未整改') {
    const orgType = params.orgType
    if (orgType !== 'simPro' && !isEnterpriseViewOrg(orgType)) return false
  }
  if (hasRectifyChild(row)) return false
  return isEduOwner(params, row)
}

function eduActionButtons(row, params) {
  const names = ['查看']
  names.push({
    name: '编辑',
    disabled: !(
      canOpenEduEdit(row, params) ||
      (getEduExecuteStatus(row) === '未整改' &&
        canSignInEdu(params && params.orgType))
    )
  })
  names.push({
    name: '删除',
    disabled: !canDeleteEdu(row, params)
  })
  if (canSignInEdu(params && params.orgType)) {
    names.push('签到')
  }
  if (canRectifyEdu(params && params.orgType)) {
    names.push({
      name: '整改',
      disabled: !canRectifyEduRow(row)
    })
  }
  return setTableButtons(names)
}

function isEnterpriseViewOrg(orgType) {
  return orgType === 'com' || orgType === 'bigPro'
}

/** 标段、劳务公司：去签到、上传影像 */
export function canSignInEdu(orgType) {
  return orgType === 'simPro' || orgType === 'laborCom'
}

function canRectifyEdu(orgType) {
  return orgType === 'simPro' || isEnterpriseViewOrg(orgType)
}

export function hasRectifyChild(row) {
  if (row && row._hasChildren) return true
  const kids = (row && row.children) || []
  return kids.length > 0
}

export function hasUnfinishedRectifyChild(row) {
  return hasRectifyChild(row)
}

export function canRectifyEduRow(row) {
  const status = getEduExecuteStatus(row)
  if (status !== '已执行' && status !== '已整改') return false
  return !hasRectifyChild(row)
}

function eduSignSheetTitle(row) {
  const team = String((row && row.teamName) || '').trim()
  const exec = String((row && row.executeTime) || '').trim()
  const plan = String((row && row.trainingTime) || '').trim()
  const date = (exec || plan).replace(/\//g, '-').slice(0, 10)
  return [team, date, '班前教育签名表'].filter(Boolean).join(' ')
}

function eduSearchCheck(params, label, fieldKey) {
  if (!params || isEduFieldLocked(params, fieldKey)) {
    return null
  }
  return label
}

function planTimePickerOptions() {
  return {
    disabledDate(time) {
      const start = new Date()
      start.setHours(0, 0, 0, 0)
      return time.getTime() < start.getTime()
    }
  }
}

function getEduVideoFormSource(params) {
  const info =
    (params &&
      params.state &&
      params.state.training &&
      params.state.training.preClassEduInfo) ||
    {}
  const row =
    (params &&
      params.state &&
      params.state.currentRow &&
      params.state.currentRow.preClassEducation) ||
    {}
  return info.uid ? info : row
}

function getTeamPeopleList(params) {
  const noExitName = Array.isArray(params.state.contractor.noExitName)
    ? params.state.contractor.noExitName
    : []
  const current = params.state.currentRow.preClassEducation || {}
  const teamUid = current.teamUid
  if (!teamUid) return noExitName
  const teamList = noExitName.filter((item) => {
    const ids = Array.isArray(item.teamUids) ? item.teamUids : []
    if (ids.length) return ids.indexOf(teamUid) !== -1
    return item.teamUid === teamUid
  })
  return teamList.length ? teamList : []
}

function getSignedCount(params) {
  const signInList = Array.isArray(params.state.signIn.signInList)
    ? params.state.signIn.signInList
    : []
  return signInList.filter((item) => {
    return item.isSignIn || item.isCheckedIn === '已签到'
  }).length
}

const preClassEducation = {
  preClassEducation: {
    name: 'preClassEducation',
    componentTypes: [
      'navigate',
      'check',
      'tables',
      'stepForm',
      'drawerTable',
      'dialogForm'
    ], // 该页面包含的组件名称，用于判断是否返回页面渲染参数
    addFun: 'training/addPreClassEducation',
    editFun: 'training/editPreClassEducation',
    getListFun: 'training/getPreClassEduList',
    deleteFun: 'training/deletePreClassEducation',
    detailPage: (params) => {
      const pages = [
      {
        buttonName: '新增',
        name: 'preClassEducation',
        type: 'stepForm',
        theme: 'preClassEducation',
        step: 'preClassEducationInfo',
        label: '新增培训教育任务',
        pageStatus: 'add'
      },
      {
        buttonName: '整改',
        name: 'preClassEducation',
        type: 'stepForm',
        theme: 'preClassEducation',
        step: 'preClassEducationInfo',
        label: '培训教育整改',
        pageStatus: 'add'
      },
      {
        buttonName: '编辑',
        name: 'preClassEducation',
        type: 'stepForm',
        theme: 'preClassEducation',
        step: 'preClassEducationInfo',
        label: '编辑培训教育任务',
        checkType: 'checkByUid',
        checkFun: 'training/getPreClassEducationInfo'
      },
      {
        buttonName: '查看',
        name: 'preClassEducation',
        type: 'stepForm',
        theme: 'preClassEducation',
        step: 'preClassEducationInfo',
        label: '查看培训教育任务',
        checkType: 'checkByUid',
        checkFun: 'training/getPreClassEducationInfo',
        pageStatus: 'check'
      },
      {
          buttonNames: canSignInEdu(params && params.orgType)
            ? ['签到人数', '签到', '去签到']
            : ['签到人数'],
        name: 'signIn',
        type: 'drawerTable',
        checkType: 'checkByRow',
        checkFun: 'training/getPreClassSignDrawer'
      },
      {
        buttonName: '查看影像',
        name: 'previewVideos',
        type: 'preview',
        theme: 'preview',
        step: 'previewInfo',
          label: (pageParams) => {
            return pageParams.formValues
              ? '播放：' +
                  pageParams.formValues.laborCompanyName +
                  '班前教育影像'
            : ''
        },
          previewParams: (pageParams) => {
            return pageParams.formValues
            ? {
                key: 'trainingVideos',
                  uid: pageParams.formValues.trainingVideos,
                type: 'video',
                name: '班前教育影像'
              }
            : null
        }
      },
      {
        buttonName: '搜索选择内容',
          name: 'selectWorkContent',
          type: 'dialogForm'
        },
        {
          buttonName: '搜索选择教育内容',
        name: 'selectContent',
        type: 'dialogForm'
      },
      {
        buttonName: '搜索选择部位',
        name: 'selectWorkPlace',
        type: 'dialogForm'
        },
        {
          buttonName: '培训影像',
          name: 'eduVideosForm',
          type: 'dialogForm',
          checkType: 'checkByUid',
          checkFun: 'training/getPreClassEducationInfo',
          pageStatus: canSignInEdu(params && params.orgType) ? 'edit' : 'check'
        }
      ]
      return pages
    },
    headButtons: () => {
      return setCheckButtons(['新增'])
    },
    queryParams: {
      orgUid: (params) => {
        if (params.orgType === 'simPro') return params.orgValue
        if (isLaborCompanyOrg(params.orgType)) {
          return getLaborParentBidUid(params)
        }
        // 企业/工程级默认不填标段，查询下级全部标段
        return ''
      },
      laborCompanyUid: (params) => {
        return isLaborCompanyOrg(params.orgType) ? params.orgValue : ''
      },
      teamUid: '',
      query: '',
      // 默认当日，对齐小程序「当日」tab
      trainingTime: () => {
        const day = daysFormat(new Date())
        return [day + ' 00:00:00', day + ' 23:59:59']
      },
      startTime: '',
      endTime: '',
      isDone: ''
    },
    selectData: (params) => {
      const fields = []
      if (!isLaborCompanyOrg(params.orgType)) {
        fields.push(
      {
        key: 'orgUid',
        label: '所属项目标段',
        type: 'select',
            options: (item) => {
              return item.state.bidSection.subBidSection
        },
        placeholder:
          params.orgType === 'com' || params.orgType === 'bigPro'
            ? '全部标段'
            : '请选择所属项目标段',
        checkByValue: true,
        clearable: params.orgType === 'com' || params.orgType === 'bigPro',
        span: 8
      },
      {
        key: 'laborCompanyUid',
        label: '所属劳务公司',
        type: 'select',
            options: (item) => {
              return item.state.bidSection.laborCompanyNames
        },
        placeholder: '请先选择所属项目标段',
        checkByValue: true,
        clearable: false,
        span: 8
          }
        )
      }
      fields.push(
      {
        key: 'teamUid',
        label: '所属班组',
        type: 'select',
          options: (item) => {
            return item.state.contractor.teamNames
        },
          placeholder: isLaborCompanyOrg(params.orgType)
            ? '请选择所属班组'
            : '请先选择所属劳务公司',
        checkByValue: true,
          clearable: isLaborCompanyOrg(params.orgType),
        span: 8
      },
      {
        key: 'trainingTime',
        label: '计划培训时间',
        type: 'time',
        timetype: 'daterange',
        format: 'yyyy-MM-dd HH:mm:ss',
        defaultTime: ['00:00:00', '23:59:59'],
        span: 8
      },
      {
        key: 'isDone',
        label: '执行状态',
        type: 'select',
        options: [
          { label: '未执行', value: '未执行' },
          { label: '已执行', value: '已执行' },
          { label: '未整改', value: '未整改' },
          { label: '已整改', value: '已整改' }
        ],
        span: 8
      },
      {
        key: 'query',
        label: '关键词',
        type: 'input',
        placeholder: '作业部位',
        span: 8
      }
      )
      return fields
    },
    expandColumn: true,
    expandAll: true,
    rowData: (params) => {
      return params.state.training.preClassEduList.result
    },
    rowTotal: (params) => {
      return params.state.training.preClassEduList.totalSize
    },
    columns: (params) => {
      const columns = [
        {
          prop: 'trainingNo',
          label: '培训编号'
        }
      ]
      // 企业/工程级展示项目标段简称，可换行，过长省略
      if (isEnterpriseViewOrg(params.orgType)) {
        columns.push({
          prop: 'bidName',
          label: '项目标段',
          minWidth: '110px',
          overflow: false,
          overflowWidth: true,
          clampLines: 2
        })
      }
      if (params.orgType !== 'laborCom') {
        columns.push({
          prop: 'laborCompanyName',
          label: '劳务公司名称',
          minWidth: '160px',
          overflow: false,
          overflowWidth: true
        })
      }
      columns.push(
        {
          prop: 'teamName',
          label: '班组名称',
          minWidth: '140px',
          overflow: false,
          overflowWidth: true
        },
        {
          prop: 'eduTime',
          label: '教育时间',
          minWidth: '180px',
          overflow: false,
          overflowWidth: true,
          align: 'center',
          stack: true,
          tooltip: {
            tipValue:
              '执行时间为空或晚于计划时间当日的话，会被统计为不合格教育'
          }
        },
        {
          prop: 'isDone',
          label: '执行状态',
          width: '76px',
          keepWidth: true,
          tag: [
            { label: '未执行', type: 'danger' },
            { label: '未整改', type: 'danger' },
            { label: '已执行', type: 'success' },
            { label: '已整改', type: 'warning' }
          ]
        },
        {
          prop: 'trainingPerson',
          label: '执行人',
          width: '72px',
          keepWidth: true
        },
        {
          prop: 'attendanceCount',
          label: '签到人数',
          width: '80px',
          keepWidth: true,
          drillDown: true
        },
        {
          prop: 'coverageRate',
          label: '人员覆盖率',
          width: '88px',
          keepWidth: true
        },
        {
          prop: 'videos',
          label: '培训影像',
          drillDown: true,
          width: '88px',
          keepWidth: true
        },
        {
          label: '操作',
          width: '360px',
          buttons: (row) => {
            // getObjAttribute 会先用页面 params 调用一次；此时返回按行计算的函数
            if (row && row.state && row.orgType !== undefined) {
              const pageParams = row
              return (dataRow) => eduActionButtons(dataRow, pageParams)
            }
            return eduActionButtons(row, params)
          }
        }
      )
      return columns
    },
    hasPagination: true,
    addArray: true,
    formParams: {
      orgUid: (params) => {
        if (isLaborCompanyOrg(params.orgType)) {
          return getLaborParentBidUid(params) || params.orgValue
        }
        if (isEnterpriseViewOrg(params.orgType)) {
          const bids =
            (params.state.bidSection && params.state.bidSection.subBidSection) ||
            []
          return (bids[0] && (bids[0].value || bids[0].orgUid)) || ''
        }
        return params.orgValue
      },
      bidName: '',
      createOrgUid: (params) => {
        return params.pageStatus === 'add' ? params.orgValue || '' : ''
      },
      laborCompanyUid: (params) => {
        return isLaborCompanyOrg(params.orgType) ? params.orgValue : ''
      },
      laborCompanyName: (params) => {
        return isLaborCompanyOrg(params.orgType) ? getLaborCompanyName(params) : ''
      },
      teamUid: '',
      teamName: '',
      trainingNo: 'BQJY' + getStringTime(new Date()),
      workPlace: '',
      workPlaceUid: '',
      checkInType: 'BQJY',
      workContent: '',
      workContents: [],
      selectedWorkPaths: [],
      trainingTime: (params) => {
        return params.pageStatus === 'add' ? defaultPlanTime() : ''
      },
      executeTime: '',
      trainingPerson: '',
      checkInNum: null,
      trainingBrief: '',
      trainingBriefs: [],
      contentPaths: [],
      executeStatus: '未执行',
      parentUid: '',
      fileLists: {
        trainingVideos: []
      }
    },
    formRules: {
      orgUid: [
        {
          required: (params) => isEnterpriseViewOrg(params.orgType),
          message: '所属项目标段不能为空',
          trigger: 'blur'
        }
      ],
      laborCompanyUid: [
        {
          required: (params) => !isLaborCompanyOrg(params.orgType),
          message: '所属劳务公司不能为空',
          trigger: 'blur'
        }
      ],
      teamUid: [
        { required: true, message: '所属班组不能为空', trigger: 'blur' },
        {
          validator: (params) => {
            return (rule, value, callback) => {
              const err =
                (params.state.training && params.state.training.teamPendingError) ||
                ''
              if (err) {
                callback(new Error(err))
                return
              }
              callback()
            }
          },
          trigger: ['change', 'blur']
        }
      ],
      trainingNo: [
        { required: true, message: '培训编号不能为空', trigger: 'blur' }
      ],
      workContent: [
        {
          required: true,
          message: '作业内容不能为空',
          trigger: 'blur'
        }
      ],
      trainingBrief: [
        {
          required: true,
          message: '教育内容不能为空',
          trigger: 'blur'
        }
      ],
      trainingPerson: [
        { required: true, message: '执行人不能为空', trigger: 'blur' }
      ],
      trainingTime: [
        { required: true, message: '计划培训时间不能为空', trigger: 'blur' },
        {
          validator: (params) => {
            return (rule, value, callback) => {
              if (!value) {
                callback()
                return
              }
              if (isEduFieldLocked(params, 'trainingTime')) {
                callback()
                return
              }
              if (params.pageStatus !== 'add') {
                const original =
                  (params.state.training &&
                    params.state.training.preClassEduInfo &&
                    params.state.training.preClassEduInfo.trainingTime) ||
                  (params.currentRow && params.currentRow.trainingTime)
                if (isUnchangedEduTime(value, original)) {
                  callback()
                  return
                }
              }
              const picked = new Date(String(value).replace(/-/g, '/'))
              if (isNaN(picked.getTime())) {
                callback(new Error('计划培训时间格式不正确'))
                return
              }
              // 已过期时提交侧会重置为提交时间，此处不再拦截
              callback()
            }
          },
          trigger: ['blur', 'change']
        }
      ],
      trainingVideos: [
        {
          validator: (params) => {
            return (rule, value, callback) => {
              const form = getEduForm(params, params.formValues)
              if (!isEduRectifyRecord(form)) {
                callback()
                return
              }
              if (params.pageStatus === 'check') {
                callback()
                return
              }
              if (params.pageStatus === 'add' && !form.uid) {
                callback()
                return
              }
              const files =
                (form.fileLists && form.fileLists.trainingVideos) ||
                form.trainingVideos ||
                value ||
                []
              const list = Array.isArray(files) ? files : []
              if (!list.length) {
                callback(new Error('请上传培训影像后再提交，上传后才算整改完成'))
                return
              }
              callback()
            }
          },
          trigger: ['blur', 'change']
        }
      ]
    },
    formData: {
      card: '班前教育信息',
      submitOnCheck: false,
      headButtons: (params) => {
        if (params.pageStatus === 'check' && hasDataReportPermission(params)) {
          return setFormButtons(['预览报表'])
        }
        return []
      },
      reportPreview: {
        reportCode: 'preClassEduRecord',
        title: '班前安全教育记录'
      },
      bottomButtons: (params) => {
        if (params.pageStatus === 'check') {
          return setFormButtons(['取消'])
        }
        const form = getEduForm(params)
        const pendingRectify = getEduExecuteStatus(form) === '未整改'
        if (
          params.pageStatus === 'add' ||
          canOpenEduEdit(form, params) ||
          (pendingRectify && canSignInEdu(params.orgType))
        ) {
          return setFormButtons(['取消', '提交'])
        }
        return setFormButtons(['取消'])
      },
      manualUpload: false,
      fields: [
        { key: 'trainingNo', label: '培训编号', type: 'input', disabled: true },
        {
          key: (params) => {
            if (!isEnterpriseViewOrg(params.orgType)) return null
            if (
              params.pageStatus === 'check' &&
              isEduFieldLocked(params, 'orgUid')
            ) {
              return 'bidName'
            }
            return 'orgUid'
          },
          label: '所属项目标段',
          type: 'select',
          options: (params) => {
            return params.state.bidSection.subBidSection
          },
          checkByValue: true,
          clearable: false,
          disabled: (params) => isEduFieldLocked(params, 'orgUid')
        },
        {
          key: (params) => {
            if (isLaborCompanyOrg(params.orgType)) return null
            if (
              params.pageStatus === 'check' &&
              isEduFieldLocked(params, 'laborCompanyUid')
            ) {
              return 'laborCompanyName'
            }
            return 'laborCompanyUid'
          },
          label: '所属劳务公司',
          type: 'select',
          options: (params) => {
            return params.state.bidSection.laborCompanyNames
          },
          checkByValue: true,
          clearable: false,
          disabled: (params) => isEduFieldLocked(params, 'laborCompanyUid')
        },
        {
          key: (params) => {
            if (
              params.pageStatus === 'check' &&
              isEduFieldLocked(params, 'teamUid')
            ) {
              return 'teamName'
            }
            return 'teamUid'
          },
          label: '所属班组',
          type: 'select',
          options: (params) => {
            return params.state.contractor.teamNames
          },
          checkByValue: true,
          echoLabelKey: 'teamName',
          clearable: false,
          disabled: (params) => isEduFieldLocked(params, 'teamUid')
        },
        {
          key: 'trainingTime',
          label: '计划培训时间',
          type: 'time',
          timetype: 'datetime',
          format: 'yyyy-MM-dd HH:mm:ss',
          pickerOptions: planTimePickerOptions(),
          disabled: (params) => isEduFieldLocked(params, 'trainingTime')
        },
        {
          key: 'executeTime',
          label: '执行培训时间',
          type: 'time',
          timetype: 'datetime',
          format: 'yyyy-MM-dd HH:mm:ss',
          disabled: true
        },
        {
          key: 'trainingPerson',
          label: '执行人',
          type: 'input',
          disabled: (params) => isEduFieldLocked(params, 'trainingPerson')
        },
        {
          key: 'workPlace',
          label: '作业部位',
          type: 'input',
          clearable: true,
          placeholder: '手动输入或搜索选择作业部位',
          disabled: (params) => isEduFieldLocked(params, 'workPlace'),
          check: (params) => eduSearchCheck(params, '搜索选择部位', 'workPlace')
        },
        {
          key: 'workContent',
          label: '作业内容',
          type: 'input',
          inputType: 'textarea',
          autosize: { minRows: 3, maxRows: 8 },
          clearable: true,
          placeholder: '手动输入或搜索选择内容',
          disabled: (params) => isEduFieldLocked(params, 'workContent'),
          check: (params) => eduSearchCheck(params, '搜索选择内容', 'workContent')
        },
        {
          key: 'trainingBrief',
          label: '教育内容',
          type: 'input',
          options: (params) => {
            return params.state.division.dataType
          },
          inputType: 'textarea',
          autosize: { minRows: 3, maxRows: 8 },
          clearable: true,
          placeholder: '手动输入或搜索选择内容',
          disabled: (params) => isEduFieldLocked(params, 'trainingBrief'),
          check: (params) =>
            eduSearchCheck(params, '搜索选择教育内容', 'trainingBrief')
        },
        {
          key: (params) => {
            const form = getEduForm(params, params.formValues)
            return form && form.uid ? 'checkInNum' : null
          },
          label: '签到人数',
          type: 'input',
          disabled: true,
          check: (params) => {
            const form = getEduForm(params, params.formValues)
            if (!form || !form.uid) return null
            return canSignInEdu(params.orgType) ? '去签到' : null
          }
        },
        {
          key: (params) => {
            const form = getEduForm(params, params.formValues)
            if (!shouldShowEduVideos(form, params)) return null
            return isEduTrainingVideoCleared(form) ? 'eduVideoExpired' : null
          },
          type: (params) => {
            const form = getEduForm(params, params.formValues)
            if (!shouldShowEduVideos(form, params)) return ''
            return isEduTrainingVideoCleared(form) ? 'tag' : ''
          },
          tag: (params) => {
            const form = getEduForm(params, params.formValues)
            if (!shouldShowEduVideos(form, params)) return ''
            return isEduTrainingVideoCleared(form) ? '培训影像已过期清除' : ''
          }
        },
        {
          key: (params) => {
            const form = getEduForm(params, params.formValues)
            if (!shouldShowEduVideos(form, params)) return null
            if (isEduTrainingVideoCleared(form)) return null
            return 'trainingVideos'
          },
          label: '培训影像',
          type: 'upload',
          url: '#',
          fileType: 'video',
          listType: 'text',
          autoUpload: false,
          showFilelist: false,
          limit: 4,
          disabled: (params) => isEduFieldLocked(params, 'trainingVideos')
        }
      ]
    },
    signIn: {
      name: 'signIn',
      title: (params) => {
        const row =
          (params.state.currentRow &&
            params.state.currentRow.preClassEducation) ||
          {}
        return eduSignSheetTitle(row) || '班前教育签名表'
      },
      size: '790px',
      direction: 'ltr',
      getListFun: 'contractor/getNoExitName',
      getParentFun: 'training/getPreClassEduList',
      emptyText: '没有查询到该机构在场人员',
      detailPage: [
        {
          buttonName: '输入签到人数',
          name: 'checkInNumForm',
          type: 'dialogForm'
        }
      ],
      headButtons: (params) => {
        if (canSignInEdu(params.orgType)) {
          return setCheckButtons(['批量签到', '输入签到人数'])
        }
        return []
      },
      tagInfo: (params) => {
        const total = getTeamPeopleList(params).length
        const signed = getSignedCount(params)
        const row =
          (params.state.currentRow &&
            params.state.currentRow.preClassEducation) ||
          {}
        const base =
          '班组内共有\xa0' + total + '\xa0人，已签到\xa0' + signed + '\xa0人'
        const inputLabel = inputCheckInLabel(row, total > 0)
        return inputLabel ? base + '，' + inputLabel : base
      },
      tagType: 'warning',
      selectColumn: (params) => {
        return canSignInEdu(params.orgType) ? true : null
      },
      selectable: () => {
        return (row) => {
          return !(row && (row.isSignIn || row.isCheckedIn === '已签到'))
        }
      },
      outListHeight: 280,
      maxheight: false,
      rowKey: () => 'personUid',
      rowData: (params) => {
        const noExitName = getTeamPeopleList(params)
        const signInList = Array.isArray(params.state.signIn.signInList)
          ? params.state.signIn.signInList
          : []
        const withSignStatus = (item) => {
          const sign = signInList.find((s) => s.personUid === item.personUid)
          return {
            ...item,
            phoneNumber:
              item.phoneNumber || item.phoneNum || (sign && sign.phoneNum) || '',
            phoneNum:
              item.phoneNum || item.phoneNumber || (sign && sign.phoneNum) || '',
            isCheckedIn: sign
              ? sign.isCheckedIn
              : item.isCheckedIn || '未签到',
            personUid:
              item.personUid || (sign && sign.personUid) || item.uid || '',
            uid: sign ? sign.uid : '',
            isSignIn: !!(
              sign &&
              (sign.isSignIn || sign.isCheckedIn === '已签到')
            )
          }
        }
          const rows = noExitName.map(withSignStatus)
          signInList.forEach((sign) => {
            if (!rows.some((item) => item.personUid === sign.personUid)) {
              rows.push(withSignStatus(sign))
            }
          })
          return rows
      },
      columns: (params) => {
        const canSign = canSignInEdu(params.orgType)
        const cols = [
              {
                label: '姓名',
                prop: 'personName',
                width: '90px',
                overflow: false
              },
              {
                label: '手机号码',
                prop: 'phoneNumber',
                width: '130px',
                overflow: false
              },
              {
                label: '工种',
                prop: 'business',
                width: '100px',
                overflow: false
              },
              {
                label: '进场时间',
                prop: 'walkinDate',
                width: '180px',
                overflow: false
              },
          canSign
            ? {
                label: '是否签到',
                prop: 'isSignIn',
                switch: 'isCheckedIn',
                width: '140px',
                overflow: false
              }
            : {
                label: '是否签到',
                prop: 'isCheckedIn',
                width: '140px',
                overflow: false
              }
        ]
        return cols
      }
    },
    eduVideosForm: {
      name: 'eduVideosForm',
      title: '培训影像',
      editFun: 'training/editPreClassEducation',
      getParentFun: 'training/getPreClassEduList',
      hasFooter: (params) => params.pageStatus !== 'check',
      manualUpload: true,
      formParams: {
        uid: (params) => getEduVideoFormSource(params).uid || '',
        trainingVideos: (params) => {
          const source = getEduVideoFormSource(params)
          return Array.isArray(source.trainingVideos)
            ? source.trainingVideos
            : []
        },
        executeTime: (params) => getEduVideoFormSource(params).executeTime || '',
        executeStatus: (params) =>
          getEduVideoFormSource(params).executeStatus || '',
        trainingTime: (params) =>
          getEduVideoFormSource(params).trainingTime || '',
        laborCompanyUid: (params) =>
          getEduVideoFormSource(params).laborCompanyUid || '',
        laborCompanyName: (params) =>
          getEduVideoFormSource(params).laborCompanyName || '',
        fileLists: (params) => {
          const source = getEduVideoFormSource(params)
          if (source.fileLists && source.fileLists.trainingVideos) {
            return source.fileLists
          }
          const videos = Array.isArray(source.trainingVideos)
            ? source.trainingVideos
            : []
          return {
            trainingVideos: videos.map((item, index) => {
              return {
                uid: item,
                videoUid: item,
                url: require('@/assets/img/video.webp'),
                name: '教育影像' + (index + 1),
                type: 'application/x-mpegURL'
              }
            })
          }
        }
      },
      formData: [
        {
          key: 'executeTime',
          label: '执行培训时间',
          type: 'time',
          timetype: 'datetime',
          format: 'yyyy-MM-dd HH:mm:ss',
          disabled: true,
          span: 20
        },
        {
          key: 'eduVideoTip',
          type: 'tag',
          tag: (params) =>
            canSignInEdu(params && params.orgType)
              ? '最多4个影像。增删成功后会同步上传文件的创建时间和已有执行时间中更早的时间给执行培训时间，请不要违规上传计划时间之前录制好的视频。'
              : '点击影像可预览播放。当前机构仅可查看，不能上传或删除。',
          isShow: (params) =>
            !isEduTrainingVideoCleared(getEduVideoFormSource(params)),
          span: 20
        },
        {
          key: 'eduVideoExpired',
          type: 'tag',
          tag: '培训影像已过期清除',
          isShow: (params) =>
            isEduTrainingVideoCleared(getEduVideoFormSource(params)),
          span: 20
        },
        {
          key: 'trainingVideos',
          label: '培训影像',
          type: 'upload',
          url: '#',
          fileType: 'video',
          listType: 'text',
          autoUpload: false,
          showFilelist: false,
          limit: 4,
          isShow: (params) =>
            !isEduTrainingVideoCleared(getEduVideoFormSource(params)),
          span: 20
        }
      ]
    },
    checkInNumForm: {
      name: 'checkInNumForm',
      title: '输入签到人数',
      getParentFun: 'training/getPreClassEduList',
      formParams: {
        checkInNum: (params) => {
          const row =
            (params.state.currentRow &&
              params.state.currentRow.preClassEducation) ||
            {}
          const raw =
            row.checkInNum != null && row.checkInNum !== ''
              ? row.checkInNum
              : row.attendanceCount
          const n = parseNonNegInt(raw)
          return n == null ? '' : n
        },
        crowdImage: '',
        fileLists: {
          crowdImage: []
        }
      },
      formRules: {
        checkInNum: [
          {
            required: true,
            message: '签到人数不能为空',
            trigger: 'blur'
          },
          {
            validator: (params) => {
              return (rule, value, callback) => {
                const row =
                  (params.state.currentRow &&
                    params.state.currentRow.preClassEducation) ||
                  {}
                const message = checkInNumError(value, teamOnSiteCap(row))
                if (message) {
                  callback(new Error(message))
                } else {
                  callback()
                }
              }
            },
            trigger: ['blur', 'change']
          }
        ]
      },
      formData: [
        {
          key: 'checkInNum',
          label: '签到人数',
          type: 'input',
          number: true,
          placeholder: '请输入大于等于0的整数，不超过班组在场人数；在场为0人时将同步到班组',
          tips: (params) => {
            const tip = params.state && params.state.crowdCountTip
            if (tip && tip.kind === 'overTeam') {
              return crowdCappedByTeamMessage(tip.recognized, tip.cap)
            }
            if (tip && tip.kind === 'overMax') {
              return (
                '已识别到' +
                tip.recognized +
                '人。单张最多识别30人，实际可能更多，请核对后确定'
              )
            }
            return '请上传现场教育图片自动识别签到人数'
          },
          tipsType: (params) => {
            const tip = params.state && params.state.crowdCountTip
            return tip ? 'danger' : ''
          },
          tipsIcon: (params) => {
            const tip = params.state && params.state.crowdCountTip
            return tip ? 'el-icon-error' : 'el-icon-info'
          },
          span: 20
        },
        {
          key: 'crowdImage',
          label: '现场照片',
          type: 'upload',
          url: '/organize/cultivate/bodycount',
          fileType: 'image',
          limit: 1,
          countBody: true,
          autoUpload: false,
          accept: 'image/jpeg,image/png,image/bmp,.jpg,.jpeg,.png,.bmp',
          uploadtip: '照片只用来数人，点确定关闭后自动清理，不会保存。',
          span: 20
        }
      ]
    },
    selectWorkPlace: {
      name: 'selectWorkPlace',
      title: '请搜索选择作业部位',
      formParams: {
        workPlaceUid: (params) => {
          return params.state.formValues.preClassEducation.workPlaceUid
        }
      },
      formData: [
        {
          key: 'workPlaceUid',
          label: '作业部位',
          type: 'cascader',
          options: (params) => {
            return params.getters['progress/boundProgressTypeTree']
          },
          filterable: true,
          showAllLevels: false,
          checkByValue: true,
          checkStrictly: false,
          emitPath: false,
          clearable: true,
          placeholder: '请搜索选择作业部位',
          span: 20
        }
      ]
    },
    selectWorkContent: {
      name: 'selectWorkContent',
      title: '请搜索选择作业内容',
      width: '760px',
      formParams: {
        workContents: (params) => {
          const form = params.state.formValues.preClassEducation || {}
          const tree = params.state.division.workContentAndRisk || []
          inferEduSelectIntoForm(form, tree)
          return form.selectedWorkPaths || []
        }
      },
      formRules: {
        workContents: [
          {
            required: true,
            type: 'array',
            message: '作业内容不能为空',
            trigger: 'change'
          }
        ]
      },
      formData: [
        {
          key: 'workContents',
          label: '作业内容',
          type: 'cascader',
          options: (params) => {
            return nestEduWorkTree(params.state.division.workContentAndRisk || [])
          },
          multiple: true,
          filterable: true,
          clearable: true,
          collapseTags: false,
          showAllLevels: true,
          maxTags: 10,
          emitPath: true,
          expandTrigger: 'hover',
          placeholder: '按层级选择，输入关键词可筛选',
          span: 20
        }
      ]
    },
    selectContent: {
      name: 'selectContent',
      title: '请搜索选择教育内容',
      width: '760px',
      formParams: {
        trainingBriefs: (params) => {
          const form = params.state.formValues.preClassEducation || {}
          const tree = params.state.division.workContentAndRisk || []
          inferEduSelectIntoForm(form, tree)
          return toEduCascadePaths(form.contentPaths || [])
        }
      },
      formRules: {
        trainingBriefs: [
          {
            required: true,
            type: 'array',
            message: '选择内容不能为空',
            trigger: 'blur'
          }
        ]
      },
      formData: [
        {
          key: 'trainingBriefs',
          label: '作业及教育内容',
          type: 'cascader',
          options: (params) => {
            const form = params.state.formValues.preClassEducation || {}
            const tree = params.state.division.workContentAndRisk || []
            inferEduSelectIntoForm(form, tree)
            const selected = form.workContents || []
            return nestEduContentTree(
              tree.filter((item) => selected.indexOf(item.label) >= 0)
            )
          },
          multiple: true,
          filterable: true,
          clearable: true,
          collapseTags: false,
          showAllLevels: true,
          maxTags: 10,
          emitPath: true,
          expandTrigger: 'hover',
          placeholder: '按层级筛选，输入关键词可快速定位，点标签可删除',
          span: 20
        }
      ]
    }
  }
}

export default preClassEducation
